import { createAction } from '@reduxjs/toolkit';
import { useState } from 'react';

import {
    resendProfileMFACode,
    resendProfileMFACodeError,
    resendProfileMFACodeSuccess,
} from '@perpay-web/fintech/actions/authentication';
import {
    sendMFACode,
    sendMFACodeError,
    sendMFACodeSuccess,
} from '@perpay-web/fintech/actions/entities/sendMFACode';
import {
    validateMFACode,
    validateMFACodeError,
    validateMFACodeSuccess,
} from '@perpay-web/fintech/actions/entities/validateMFACodes';
import { useActionObservableCallback } from '@perpay-web/hooks/useActionObservableCallback';
import { hookAction$ } from '@perpay-web/services/singletons';
import { createContextProvider } from '@perpay-web/utils/componentUtils';

/** @type {import('@reduxjs/toolkit').ActionCreatorWithPayload<{ {ottpUuid: string}  }>} */
export const sendMFACodeContextSuccess = createAction(
    'SEND_MFA_CODE_CONTEXT_SUCCESS',
);

/** @type {import('@reduxjs/toolkit').ActionCreatorWithPayload<{ {ottpUuid: string}  }>} */
export const resendMFACodeContextSuccess = createAction(
    'RESEND_MFA_CODE_CONTEXT_SUCCESS',
);

/** @type {import('@reduxjs/toolkit').ActionCreatorWithPayload<{ {send: Error}  }>} */
export const sendMFAErrorHandling = createAction('SEND_MFA_ERROR_HANDLING');

/** @type {import('@reduxjs/toolkit').ActionCreatorWithPayload<{ {attempt: Error}  }>} */
export const attemptValidateMFAErrorHandling = createAction(
    'ATTEMPT_VALIDATE_MFA_ERROR_HANDLING',
);

/** @type {import('@reduxjs/toolkit').ActionCreatorWithPayload<{ {resend: Error}  }>} */
export const resendMFAErrorHandling = createAction('RESEND_MFA_ERROR_HANDLING');

/** @type {import('@reduxjs/toolkit').ActionCreatorWithPayload<{ {throttle: Error}  }>} */
export const throttleMFAErrorHandling = createAction(
    'THROTTLE_MFA_ERROR_HANDLING',
);

/** @type {import('@reduxjs/toolkit').ActionCreatorWithPayload} */
export const validateMFACodeContextSuccess = createAction(
    'VALIDATE_MFA_CODE_CONTEXT_SUCCESS',
);

const useProfileMFA = () => {
    const [otpUuid, setOtpUuid] = useState(null);
    const [validationSuccess, setValidationSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [errors, setMFAErrors] = useState({
        send: null,
        attempt: null,
        resend: null,
        throttle: null,
    });

    return {
        otpUuid,
        setOtpUuid,
        validationSuccess,
        setValidationSuccess,
        isLoading,
        setIsLoading,
        errors,
        setMFAErrors,
    };
};

const providerFactory = (Context) => {
    const Provider = ({ children }) => {
        const hook = useProfileMFA();

        useActionObservableCallback(
            hookAction$,
            sendMFACodeContextSuccess().type,
            (value) => hook.setOtpUuid(value),
        );

        useActionObservableCallback(
            hookAction$,
            sendMFAErrorHandling().type,
            (value) => hook.setMFAErrors({ ...hook.errors, send: value }),
        );

        useActionObservableCallback(
            hookAction$,
            attemptValidateMFAErrorHandling().type,
            (value) => hook.setMFAErrors({ ...hook.errors, attempt: value }),
        );

        useActionObservableCallback(
            hookAction$,
            resendMFACodeContextSuccess().type,
            (value) => hook.setOtpUuid(value),
        );

        useActionObservableCallback(
            hookAction$,
            resendMFAErrorHandling().type,
            (value) => hook.setMFAErrors({ ...hook.errors, resend: value }),
        );

        useActionObservableCallback(
            hookAction$,
            throttleMFAErrorHandling().type,
            (value) => hook.setMFAErrors({ ...hook.errors, throttle: value }),
        );

        useActionObservableCallback(
            hookAction$,
            validateMFACodeContextSuccess().type,
            () => hook.setValidationSuccess(true),
        );

        useActionObservableCallback(hookAction$, sendMFACode().type, () =>
            hook.setIsLoading(true),
        );

        useActionObservableCallback(
            hookAction$,
            sendMFACodeSuccess().type,
            () => hook.setIsLoading(false),
        );

        useActionObservableCallback(hookAction$, sendMFACodeError().type, () =>
            hook.setIsLoading(false),
        );

        useActionObservableCallback(hookAction$, validateMFACode().type, () =>
            hook.setIsLoading(true),
        );

        useActionObservableCallback(
            hookAction$,
            validateMFACodeSuccess().type,
            () => hook.setIsLoading(false),
        );

        useActionObservableCallback(
            hookAction$,
            validateMFACodeError().type,
            () => hook.setIsLoading(false),
        );

        useActionObservableCallback(
            hookAction$,
            resendProfileMFACode().type,
            () => hook.setIsLoading(true),
        );

        useActionObservableCallback(
            hookAction$,
            resendProfileMFACodeSuccess().type,
            () => hook.setIsLoading(false),
        );

        useActionObservableCallback(
            hookAction$,
            resendProfileMFACodeError().type,
            () => hook.setIsLoading(false),
        );

        return <Context.Provider value={hook}>{children}</Context.Provider>;
    };

    return Provider;
};

export const [UseProfileMFAContextProvider, useProfileMFAContext] =
    createContextProvider(providerFactory);
